import { create } from 'zustand';
import { devtools, subscribeWithSelector } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { createChatWidgetSlice } from './features/chatWidget';
import { createQuoteStepperSlice } from './features/quoteStepper';
import { createUISlice } from './features/UI';
import type { AppState } from './useAppStore.types';

// #region Reseting slices
/**
 * A set of functions used to reset slices of the store.
 *
 * @example
 * sliceResetFns.add(() => get().resetStepper());
 */
export const sliceResetFunctions = new Set<() => void>();

/**
 * Calls all registered slice reset functions to reset the app store.
 */
export const resetAppStore = () => {
  sliceResetFunctions.forEach((resetFn) => {
    resetFn();
  });
};
// #endregion

/**
 * Access the store that holds every slice
 */
export const useAppStore = create<AppState>()(
  devtools(
    immer(
      subscribeWithSelector((...a) => {
        const store: AppState = {
          UI: createUISlice(...a),
          quoteStepper: createQuoteStepperSlice(...a),
          chatWidget: createChatWidgetSlice(...a),
        };

        // Register the slice reset functions to global
        Object.values(store).forEach((slice) =>
          sliceResetFunctions.add(() => slice?.actions?.reset()),
        );

        return store;
      }),
    ),
    { name: 'ConsumerPortalStore' },
  ),
);
